import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AlertWithTimestamp } from '../../../../components/AlertWithTimestamp';
import { extractApiErrorMessage } from '../../../../utils/extractApiErrorMessage';
import { NETWORK_PRICES_QUERY_FILTER, reloadNetworkLine, startLineReload } from '../networkLineReload';
import { isLinePending, useNetworkPricesStore } from '../NetworkPrices.store';
import { editNetworkAveragePrice } from '../services';
import { EditedNetworkClusterPrice, LineKey, NetworkPriceLine, NetworkPricesPage } from '../types';
import { calcNetworkAveragePrice, confirmClusterPrice, getLineCellIds, getLineKey } from '../utils';

type EditNetworkPriceVariables = { line: NetworkPriceLine; price: number };

type EditNetworkPriceActions = {
  commitNetworkPrice: (line: NetworkPriceLine) => void;
};

const NO_DRAFTS = {};

// Status and margin come from the back-end; brokenLimit only arrives with the line reload.
const applySavedClusterPrices = (page: NetworkPricesPage, lineKey: LineKey, savedPrices: EditedNetworkClusterPrice[]): NetworkPricesPage => {
  const savedByCellId = new Map(savedPrices.map(saved => [saved.productsToBePricedId, saved]));

  return {
    ...page,
    content: page.content.map(line => {
      if (getLineKey(line) !== lineKey) return line;
      const clusters = line.clusters.map(cluster => {
        const saved = cluster.productsToBePricedId === null ? undefined : savedByCellId.get(cluster.productsToBePricedId);
        if (!saved) return cluster;
        return { ...confirmClusterPrice(cluster, saved.price), priceStatus: saved.priceStatus, newMargin: saved.newMargin };
      });
      const confirmedLine = { ...line, clusters };
      return { ...confirmedLine, averagePrice: calcNetworkAveragePrice(confirmedLine, NO_DRAFTS) };
    }),
  };
};

// The back-end rejects anything but a positive price.
const isValidNetworkPrice = (draft: number | null | undefined): draft is number => draft !== undefined && draft !== null && Number.isFinite(draft) && draft > 0;

export const useEditNetworkPrice = (): EditNetworkPriceActions => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: ({ line, price }: EditNetworkPriceVariables) => editNetworkAveragePrice(getLineCellIds(line), price),
    onSuccess: async (savedPrices, { line }) => {
      const lineKey = getLineKey(line);
      const lineReload = startLineReload(queryClient, lineKey);

      await queryClient.cancelQueries(NETWORK_PRICES_QUERY_FILTER);
      queryClient.setQueriesData<NetworkPricesPage>(NETWORK_PRICES_QUERY_FILTER, cached => cached && applySavedClusterPrices(cached, lineKey, savedPrices));
      await reloadNetworkLine(queryClient, line, lineReload);

      useNetworkPricesStore.getState().clearLineDrafts(lineKey, getLineCellIds(line));
      lineReload.finish();
    },
    onError: (error: unknown, { line }) => {
      AlertWithTimestamp({ message: extractApiErrorMessage(error) ?? `Erro ao salvar preço médio do produto ${line.productId}` });
    },
    onSettled: (_data, _error, { line }) => {
      useNetworkPricesStore.getState().finishPendingLine(getLineKey(line));
    },
  });

  const commitNetworkPrice = useCallback(
    (line: NetworkPriceLine) => {
      const lineKey = getLineKey(line);
      const cellIds = getLineCellIds(line);
      const state = useNetworkPricesStore.getState();
      // The blur of an editor removed by a resize remount is not the user leaving the field.
      if (state.isTableRemounting || isLinePending(state, lineKey, cellIds)) return;
      const draft = state.networkDraftPrices[lineKey];

      // Without a typed draft the user only left the field. A draft left by a failed save stays and is retried.
      if (!isValidNetworkPrice(draft) || cellIds.length === 0) {
        state.clearNetworkDraftPrice(lineKey);
        return;
      }

      state.startPendingLine(lineKey);
      mutate({ line, price: draft });
    },
    [mutate],
  );

  return { commitNetworkPrice };
};
